import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Circle, Sparkles, ChevronRight, X, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

/* First-login welcome checklist — shown at the top of /admin Dashboard.

   Why this component exists:
   The onboarding pipeline ends at /onboard creating the studio + redirecting
   to /admin. Without guidance, new photographers land on an empty dashboard,
   bounce, and never come back. This walks them through 5 concrete first
   actions, auto-checking each as they complete it (data-driven, not
   localStorage-driven, so it self-heals across devices).

   Visibility rule:
   - Hidden when all 5 items are done (no clutter for power users)
   - Hidden after the photographer dismisses it (localStorage per-tenant)
   - Otherwise shown collapsed by default (single tile with progress) so it
     doesn't dominate the dashboard.  Click to expand. */

const DISMISS_KEY = (studioId) => `lumetier_checklist_dismissed_${studioId}`;

export default function FirstLoginChecklist({ studioId }) {
  const [status, setStatus] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (!studioId) return;
    setDismissed(localStorage.getItem(DISMISS_KEY(studioId)) === "1");
  }, [studioId]);

  const load = () => {
    api.get("/admin/onboarding/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  };

  useEffect(load, []);

  if (!status || dismissed) return null;
  if (status.completed === status.total) return null; // hide once everything's checked

  function dismiss() {
    if (studioId) localStorage.setItem(DISMISS_KEY(studioId), "1");
    setDismissed(true);
  }

  async function seed() {
    if (!window.confirm(
      "Add sample data to your studio?\n\nThis creates 3 services, 2 customers, 1 confirmed booking, and 1 draft gallery — useful for poking around before you have real clients. You can delete it any time.",
    )) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-sample-data");
      toast.success(`Sample data added (${data.created.services} services, ${data.created.bookings} booking, ${data.created.galleries} gallery).`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add sample data");
    } finally {
      setSeeding(false);
    }
  }

  const pct = Math.round((status.completed / status.total) * 100);

  return (
    <div
      className="relative bg-gradient-to-br from-amber-50/60 via-card to-card border border-amber-200 overflow-hidden"
      data-testid="first-login-checklist"
    >
      <button
        onClick={dismiss}
        title="Dismiss"
        data-testid="checklist-dismiss"
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground z-10"
      >
        <X className="w-4 h-4" />
      </button>

      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left px-5 sm:px-6 py-4 flex items-center gap-4"
        data-testid="checklist-toggle"
      >
        <div className="w-10 h-10 shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-[0.22em] text-amber-700 font-medium">Welcome to Lumetier</div>
          <div className="text-base font-medium">
            {status.completed} of {status.total} steps complete
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-3 pr-7">
          {/* Editorial gold arc progress — 36px ring, stroke-dasharray */}
          <svg width="36" height="36" viewBox="0 0 36 36" className="-rotate-90" aria-hidden="true">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--gold-foil, #C9A961)" strokeOpacity="0.18" strokeWidth="1.5" />
            <circle
              cx="18" cy="18" r="15.5"
              fill="none"
              stroke="var(--gold-base, #C9A961)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray={`${(pct / 100) * 97.39} 97.39`}
              style={{ transition: "stroke-dasharray 600ms ease" }}
            />
          </svg>
          <div className="text-xs text-muted-foreground tabular-nums w-8 text-right font-serif italic">{pct}%</div>
        </div>
        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-amber-100 px-5 sm:px-6 py-5 space-y-1" data-testid="checklist-items">
          {status.items.map((it) => (
            <Link
              key={it.key}
              to={it.href}
              className={`group flex items-center gap-3 py-2.5 px-2 -mx-2 rounded transition-colors ${
                it.done ? "" : "hover:bg-amber-50/50"
              }`}
              data-testid={`checklist-item-${it.key}`}
            >
              {it.done ? (
                <Check className="w-5 h-5 text-emerald-600 shrink-0" />
              ) : (
                <Circle className="w-5 h-5 text-muted-foreground/40 shrink-0 group-hover:text-amber-600 transition-colors" />
              )}
              <span className={`flex-1 text-sm ${it.done ? "line-through text-muted-foreground" : ""}`}>
                {it.label}
              </span>
              {!it.done && (
                <span className="text-[10px] uppercase tracking-[0.22em] text-amber-700 opacity-0 group-hover:opacity-100 transition-opacity">
                  Do it now →
                </span>
              )}
            </Link>
          ))}

          {status.can_seed_sample && (
            <div className="pt-4 mt-2 border-t border-amber-100">
              <button
                onClick={seed}
                disabled={seeding}
                data-testid="checklist-seed-btn"
                className="inline-flex items-center gap-2 px-4 h-9 text-xs uppercase tracking-[0.22em] border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 disabled:opacity-50"
              >
                <Wand2 className="w-3.5 h-3.5" />
                {seeding ? "Seeding…" : "Add sample data to explore"}
              </button>
              <div className="text-xs text-muted-foreground mt-2">
                Or — if you just want to see what a populated dashboard feels like, add three sample services + a booking + a gallery. Delete any time.
              </div>
            </div>
          )}

          {status.seeded_sample_at && (
            <div className="pt-3 mt-1 text-xs text-muted-foreground border-t border-amber-100">
              Sample data added {new Date(status.seeded_sample_at).toLocaleDateString()} — you can delete it whenever you&apos;re ready to go live.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
